import * as React from 'react';
import HTMLReactParser from 'html-react-parser';
import {useParams} from 'react-router-dom';
import millify from 'millify';
import {Col, Row, Typography, Select} from 'antd';
import {
  MoneyCollectOutlined, DollarCircleOutlined, FundOutlined, ExclamationCircleOutlined, StopOutlined,
  TrophyOutlined, CheckOutlined, NumberOutlined, ThunderboltOutlined
} from '@ant-design/icons';

import {useGetCryptoDetailsQuery, useGetCryptoHistoryQuery} from '../../services/cryptoApi';
import {Loading, LineChart} from "../../components";
import {ICoin} from "../../models/ICoins";
import KeyBuilder from "../../utils/KeyBuilder";

const {Title, Text} = Typography;
const {Option} = Select;

const time = ['3h', '24h', '7d', '30d', '1y', '3m', '3y', '5y'];

interface CryptoDetailsProps {

}

const CryptoDetails: React.FC<CryptoDetailsProps> = ({}) => {

  const {coinId} = useParams();
  const [timePeriod, setTimePeriod] = React.useState("7d");
  const {data, isFetching} = useGetCryptoDetailsQuery(coinId);
  const [coinHistory, setCoinHistory] = React.useState(null);

  const {data: coinHistoryData, refetch} = useGetCryptoHistoryQuery({coinId, timePeriod});
  const cryptoDetails: ICoin = data?.data?.coin;

  React.useEffect(() => {
    setCoinHistory(coinHistoryData)
  }, [timePeriod, coinHistoryData]);

  if (isFetching) return <Loading/>


  const stats = [
    {
      title: 'Price to USD',
      value: `$ ${cryptoDetails?.price && millify(+cryptoDetails?.price)}`,
      icon: <DollarCircleOutlined/>
    },
    {title: 'Rank', value: cryptoDetails?.rank, icon: <NumberOutlined/>},
    {
      title: '24h Volume',
      value: `$ ${cryptoDetails["24hVolume"] && millify(+cryptoDetails["24hVolume"])}`,
      icon: <ThunderboltOutlined/>
    },
    {
      title: 'Market Cap',
      value: `$ ${cryptoDetails?.marketCap && millify(+cryptoDetails?.marketCap)}`,
      icon: <DollarCircleOutlined/>
    },
    {
      title: 'All-time-high(daily avg.)',
      value: `$ ${cryptoDetails?.allTimeHigh?.price && millify(+cryptoDetails?.allTimeHigh?.price)}`,
      icon: <TrophyOutlined/>
    },
  ];

  const genericStats = [
    {title: 'Number Of Markets', value: cryptoDetails?.numberOfMarkets, icon: <FundOutlined/>},
    {title: 'Number Of Exchanges', value: cryptoDetails?.numberOfExchanges, icon: <MoneyCollectOutlined/>},
    {
      title: 'Approved Supply',
      value: cryptoDetails?.supply?.confirmed ? <CheckOutlined/> : <StopOutlined/>,
      icon: <ExclamationCircleOutlined/>
    },
    {
      title: 'Total Supply',
      value: `$ ${cryptoDetails?.supply?.total && millify(+cryptoDetails?.supply?.total)}`,
      icon: <ExclamationCircleOutlined/>
    },
    {
      title: 'Circulating Supply',
      value: `$ ${cryptoDetails?.supply?.circulating && millify(+cryptoDetails?.supply?.circulating)}`,
      icon: <ExclamationCircleOutlined/>
    },
  ];

  return (
    <Col className="coin-detail-container">
      <Col className="coin-heading-container">
        <Title level={2} className="coin-name">
          {data?.data?.coin.name} ({data?.data?.coin.symbol}) Price
        </Title>
        <p>{cryptoDetails.name} live price in US Dollar (USD). View value statistics, market cap and supply.</p>
      </Col>
      <Select defaultValue="7d"
              className="select-timeperiod"
              placeholder="Select Period of Time"
              onChange={(value) => setTimePeriod(value)}
              value={timePeriod}
      >
        {time.map((date) => <Option key={date}>{date}</Option>)}
      </Select>

      <LineChart
        coinHistory={coinHistory}
        currentPrice={millify(+cryptoDetails?.price)}
        coinName={cryptoDetails?.name}
      />

      <Col className="stats-container">
        <Col className="coin-value-statistics-heading">
          <Col className="coin-value-statistics-heading">
            <Title level={3} className="coin-details-heading">{cryptoDetails.name} Value Statistic</Title>
            <p>An overview showing the statistics of {cryptoDetails.name}, such as the base and quote currency, the
              rank, and trading volume.</p>
          </Col>
          {stats.map(({icon, title, value}) => (
            <Col className="coin-stats" key={KeyBuilder.build}>
              <Col className="coin-stats-name">
                <Text>{icon}</Text>
                <Text>{title}</Text>
              </Col>
              <Text className="stats">{value ?? "0.0"}</Text>
            </Col>
          ))}
        </Col>
        <Col className="other-stats-info">
          <Col className="coin-value-statistics-heading">
            <Title level={3} className="coin-details-heading">Other Stats Info</Title>
            <p>An overview showing the statistics of {cryptoDetails.name}, such as the number of markets, exchanges and
              all info about supply.</p>
          </Col>
          {genericStats.map(({icon, title, value}) => (
            <Col className="coin-stats" key={KeyBuilder.build}>
              <Col className="coin-stats-name">
                <Text>{icon}</Text>
                <Text>{title}</Text>
              </Col>
              <Text className="stats">{value}</Text>
            </Col>
          ))}
        </Col>
      </Col>
      <Col className="coin-desc-link">
        <Row className="coin-desc">
          <Title level={3} className="coin-details-heading">What is {cryptoDetails.name}?</Title>
          {HTMLReactParser(cryptoDetails.description)}
        </Row>
        <Col className="coin-links">
          <Title className="coin-details-heading">{cryptoDetails.name} Links</Title>
          {cryptoDetails.links.map((link) => (
            <Row className="coin-link" key={link.name}>
              <Title level={5} className="link-name">{link.type}</Title>
              <a href={link.url} target="_blank" rel="noreferrer">{link.name}</a>
            </Row>
          ))}
        </Col>
      </Col>
    </Col>
  )
};

export default React.memo<CryptoDetailsProps>(CryptoDetails);